import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { mediaSrc } from "../lib/media";
import { t } from "../lib/i18n";
import Seo from "../components/Seo";
import DemoBadge from "../components/DemoBadge";
import { SectionHead, PersonCard, MediaTile, EventCard, PartnerCard, ChallengeCard, Loader } from "../components/Blocks";

const HERO_IMG =
  "https://images.unsplash.com/photo-1707079422190-cd6d7d84d657?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Home() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/home").then(({ data }) => setData(data)).catch(() => setData({}));
  }, []);

  if (!data) return <Loader />;

  const partner = (data.partners || [])[0];

  return (
    <div data-testid="home-page">
      <Seo
        title="La mémoire vivante de Vytali"
        description="VytaliBoots capte, amplifie et préserve les moments réels de Vytali : les gens, les partenaires, les événements et les expériences, en photo et en vidéo."
        path="/"
        kind="home"
      />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden" data-testid="home-hero">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 vb-scrim" />
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 pb-20 lg:pb-28 w-full">
          <div className="vb-label mb-6">Écosystème Vytali</div>
          <h1 className="vb-display text-5xl sm:text-6xl lg:text-8xl font-black leading-[0.9] tracking-tight">
            VYTALI<span className="text-[#FF2E63]">BOOTS</span>
          </h1>
          <p className="mt-8 vb-display text-2xl sm:text-3xl lg:text-4xl text-white/90 max-w-3xl">
            {t("home.hero_primary")}
          </p>
          <p className="mt-4 text-base md:text-lg text-neutral-400 tracking-wide">{t("home.hero_support")}</p>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/rejoindre" data-testid="hero-join-btn" className="vb-btn vb-btn-primary">
              {t("home.cta_join")} <ArrowRight size={16} />
            </Link>
            <Link to="/decouvrir" data-testid="hero-discover-btn" className="vb-btn vb-btn-ghost">
              {t("home.cta_discover")}
            </Link>
          </div>
        </div>
      </section>

      {/* FACES */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 lg:py-32" data-testid="home-faces">
        <SectionHead label="Visages" title={t("home.faces")}>
          Membres, Ambassadeurs et Créateurs mis en lumière. Un Ambassadeur Vytali contribue par sa
          présence, sa communauté et ses contenus — jamais par obligation commerciale.
        </SectionHead>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
          {(data.faces || []).map((p, i) => (
            <PersonCard key={p.id} person={p} testid={`home-face-card-${i}`} />
          ))}
        </div>
      </section>

      {/* BOOTS RIGHT NOW */}
      <section className="bg-[#070707] border-y border-white/5" data-testid="home-now">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionHead
            label="En direct"
            title={t("home.now")}
            action={
              <Link to="/decouvrir" data-testid="home-now-all" className="vb-btn vb-btn-ghost !py-2.5">
                Tout voir
              </Link>
            }
          >
            Les derniers contenus visuels de la communauté. Le contenu de démonstration est identifié.
          </SectionHead>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4">
            {(data.media || []).map((m, i) => (
              <MediaTile
                key={m.id}
                item={m}
                testid={`home-media-tile-${i}`}
                className={i === 0 ? "aspect-square md:col-span-2 md:row-span-2 md:aspect-auto" : "aspect-square"}
              />
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCES */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 lg:py-32" data-testid="home-experiences">
        <SectionHead
          label="Expériences"
          title={t("home.experiences")}
          action={
            <Link to="/evenements" data-testid="home-events-all" className="vb-btn vb-btn-ghost !py-2.5">
              Tous les événements
            </Link>
          }
        >
          VytaliDays, VytaliNights, activations partenaires, lancements et séances photo.
        </SectionHead>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
          {(data.events || []).map((e, i) => (
            <EventCard key={e.id} event={e} testid={`home-event-card-${i}`} />
          ))}
        </div>
      </section>

      {/* PARTNER SPOTLIGHT */}
      {partner && (
        <section className="bg-[#070707] border-y border-white/5" data-testid="home-partner-spotlight">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            <div className="relative aspect-[4/3] overflow-hidden group">
              {partner.hero_media_url && (
                <img src={mediaSrc(partner.hero_media_url)} alt={partner.business_name} loading="lazy" className="w-full h-full object-cover vb-zoom" />
              )}
            </div>
            <div>
              <div className="vb-label mb-4">{t("home.partner_spotlight")}</div>
              <div className="flex items-center gap-3">
                <h2 className="vb-display text-3xl lg:text-5xl font-bold">{partner.business_name}</h2>
                <DemoBadge show={partner.is_demo} />
              </div>
              <p className="mt-6 text-base text-neutral-300 leading-relaxed">{partner.atmosphere_description}</p>
              <p className="mt-4 text-sm text-neutral-500">
                {partner.category} · {partner.city}
                {partner.location ? ` · ${partner.location}` : ""}
              </p>
              <Link to={`/partenaires/${partner.slug}`} data-testid="home-partner-link" className="vb-btn vb-btn-primary mt-10">
                Vivre l'ambiance <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CHALLENGES */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 lg:py-32" data-testid="home-challenges">
        <SectionHead
          label="Communauté"
          title={t("home.challenges")}
          action={
            <Link to="/challenges" data-testid="home-challenges-all" className="vb-btn vb-btn-ghost !py-2.5">
              Tous les challenges
            </Link>
          }
        >
          La participation, la créativité et l'authenticité comptent — pas seulement le nombre d'abonnés.
        </SectionHead>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
          {(data.challenges || []).map((c, i) => (
            <ChallengeCard key={c.id} challenge={c} testid={`home-challenge-card-${i}`} />
          ))}
        </div>
      </section>

      {/* CREATORS */}
      <section className="bg-[#070707] border-y border-white/5" data-testid="home-creators">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionHead
            label="Réseau"
            title={t("home.creators")}
            action={
              <Link to="/createurs" data-testid="home-creators-all" className="vb-btn vb-btn-ghost !py-2.5">
                Voir le réseau
              </Link>
            }
          >
            Photographes, vidéastes, créateurs, influenceurs et talents.
          </SectionHead>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {(data.creators || []).map((p, i) => (
              <PersonCard key={p.id} person={p} testid={`home-creator-card-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS GRID */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 lg:py-32" data-testid="home-partners">
        <SectionHead label="Lieux" title="Partenaires Vytali">
          Ce que ça fait d'y être : atmosphère, gens, moments.
        </SectionHead>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
          {(data.partners || []).map((p, i) => (
            <PartnerCard key={p.id} partner={p} testid={`home-partner-card-${i}`} />
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-white/10" data-testid="home-final-cta">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 lg:py-40">
          <h2 className="vb-display text-4xl sm:text-5xl lg:text-7xl font-black leading-tight max-w-4xl">
            {t("home.final_cta")}
          </h2>
          <div className="mt-14 flex flex-wrap gap-4">
            <Link to="/rejoindre?interet=ambassador" data-testid="cta-ambassador" className="vb-btn vb-btn-primary">
              {t("home.be_ambassador")}
            </Link>
            <Link to="/rejoindre?interet=creator_influencer" data-testid="cta-creator" className="vb-btn vb-btn-ghost">
              {t("home.be_creator")}
            </Link>
            <Link to="/rejoindre?interet=model_talent" data-testid="cta-talent" className="vb-btn vb-btn-ghost">
              {t("home.be_talent")}
            </Link>
            <Link to="/rejoindre?interet=partner_business" data-testid="cta-partner" className="vb-btn vb-btn-ghost">
              {t("home.be_partner")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
